/**
 * 取證交付的共用工具（批108）。
 *
 * 三個 realm（我方後台／代理商／客戶 portal）的「下載授權檔」與「複製憑證」兩顆按鈕必須
 * 逐字一致，所以只寫這一份。
 *
 * ## 為什麼不用 `<a href>` 直接下載
 *
 * 後台 SPA 的 token 在 `Authorization` 標頭（`api/client.ts`），瀏覽器導覽帶不了它——
 * 所以取證端點回的是 JSON blob，前端拿到文字後自己組 `Blob` 存檔。
 * 這也是為什麼「下載」與「複製」共用同一支端點：兩種帶走方式都會記到同一筆稽核。
 */

/** 授權檔的標準檔名——**與安裝手冊一致**（放進站台的 `license/` 目錄即可生效）。 */
export const LICENSE_FILENAME = 'license.ipolic'
/** 加購子憑證的標準檔名（貼進站台〈系統 → 授權 → 手動安裝 .lic〉）。 */
export const ADDON_FILENAME = 'addon.ipoadd'

/**
 * 把一段文字存成檔案。
 *
 * ★ 憑證原文結尾補一個換行：客戶常直接 `cat` 到 `license/` 目錄，沒有換行的檔案在某些
 *   編輯器裡會與下一行黏在一起。
 */
export function saveTextFile(text: string, filename: string): void {
  const blob = new Blob([text.endsWith('\n') ? text : `${text}\n`], {
    type: 'text/plain;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * 把一個 Blob 存成檔案（批153 批B：待審 plugin 包的下載）。原樣存，不補換行——
 * 二進位檔多一個位元組 sha256 就對不上。
 */
export function saveBlobFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * 從 `Content-Disposition` 取檔名（`filename*=UTF-8''…` 優先，其次 `filename="…"`）；取不到回 null。
 * 只留檔名本身——路徑分隔字元一律拿掉（伺服器回什麼都不該讓瀏覽器存到別的目錄去）。
 */
export function filenameFromDisposition(header: string | null | undefined): string | null {
  if (!header) return null
  let name: string | null = null
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header)
  if (star) {
    try {
      name = decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''))
    } catch {
      name = null
    }
  }
  if (!name) {
    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(header)
    if (plain) name = (plain[2] ?? plain[1]).trim()
  }
  if (!name) return null
  const base = name.split(/[\\/]/).pop()?.trim() ?? ''
  return base || null
}

/**
 * 複製到剪貼簿。**回 `false` 代表這個環境沒有剪貼簿權限**，呼叫端要退化成把原文顯示
 * 出來讓人手動選取。
 *
 * ★★ 批108 修掉的一個真 bug：舊寫法是 `await navigator.clipboard?.writeText(text)`
 *    ——optional chaining 在沒有 clipboard API 時**靜默成功**（整個運算式是 undefined，
 *    不會丟例外），於是介面彈出「已複製憑證」但剪貼簿其實是空的。非安全來源（http）
 *    與部分瀏覽器設定都會踩到。
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
